import React, { useState } from 'react';
import { Trash2, Loader, Plus, Globe, Pencil, Check, X, Lock, Zap, Users, TrendingUp, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export default function SourcesView({ sources, onAdd, onUpdate, onDelete, onSourceClick }) {
  const { t } = useLanguage();
  const [val, setVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [sourceToDelete, setSourceToDelete] = useState(null);

  const handleAdd = async () => {
    if (!val.trim()) return;
    setLoading(true);
    await onAdd(val);
    setVal('');
    setLoading(false);
  };

  const startEdit = (source) => {
    setEditingId(source.id);
    setEditVal(source.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditVal('');
  };

  const saveEdit = async (id) => {
    if (!editVal.trim()) return;
    await onUpdate(id, editVal.trim());
    setEditingId(null);
    setEditVal('');
  };

  const handleSourceClick = (source) => {
    if (onSourceClick && !editingId) {
      onSourceClick(source);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="bg-white p-4 md:p-6 rounded-xl border shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Globe className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Manage Sources</h2>
            <p className="text-xs text-gray-500">{sources.length} Registered Sources</p>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            className="flex-1 border p-2 md:p-3 rounded-lg text-sm"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder={t('newSourcePlaceholder')}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <button
            onClick={handleAdd}
            disabled={loading}
            className="bg-blue-600 text-white px-4 md:px-6 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
          >
            {loading ? <Loader className="animate-spin w-4 h-4" /> : <Plus className="w-4 h-4" />}
            <span className="hidden sm:inline">{t('add')}</span>
          </button>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
        {sources.map((s) => {
          // Use server-provided stats
          const pqTotal = s.totalPrequalifications || 0;
          const pqEnlisted = s.enlistedPrequalifications || 0;
          const pqRate = s.pqConversionRate || 0;

          const tenderTotal = s.totalTenders || 0;
          const tenderAwarded = s.awardedTenders || 0;
          const tenderRate = s.tenderConversionRate || 0;

          return (
            <div
              key={s.id}
              onClick={() => handleSourceClick(s)}
              className={`p-3 md:p-4 rounded-xl border shadow-sm transition-all ${s.isIntegration
                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                : 'bg-white'
                } ${!editingId ? 'cursor-pointer hover:shadow-md hover:border-blue-300' : ''}`}
            >
              {editingId === s.id && !s.isIntegration ? (
                <div className="flex items-center gap-2">
                  <input
                    className="flex-1 border p-2 rounded-lg text-sm font-medium"
                    value={editVal}
                    onChange={(e) => setEditVal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveEdit(s.id);
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); saveEdit(s.id); }}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg flex-shrink-0"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {s.isIntegration && (
                        <Zap className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      )}
                      <span className="font-bold text-sm md:text-base truncate">{s.name}</span>
                      {s.isIntegration && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                          {t('integration')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {s.isIntegration ? (
                        <div
                          className="p-1.5 text-gray-400 flex-shrink-0"
                          title={t('integrationsLocked')}
                        >
                          <Lock className="w-4 h-4" />
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); startEdit(s); }}
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg flex-shrink-0"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSourceToDelete(s); }}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400 ml-1" />
                    </div>
                  </div>

                  {/* Stats Section */}
                  <div className="mt-3 pt-3 border-t space-y-2">
                    
                    {/* Prequalifications Stats */}
                    <div className="flex items-center justify-between bg-blue-50/50 p-2 rounded-lg">
                      <div className="flex items-center gap-1.5 text-blue-700">
                        <Users className="w-3.5 h-3.5" />
                        <span className="text-[10px] uppercase font-bold tracking-wider">Prequals</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <div className="text-gray-600"><span className="font-bold text-gray-900">{pqTotal}</span> Total</div>
                        <div className="text-gray-600"><span className="font-bold text-gray-900">{pqEnlisted}</span> Enlisted</div>
                        <div className={`font-bold ${pqRate >= 20 ? 'text-green-600' : pqRate > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {pqRate.toFixed(1)}%
                        </div>
                      </div>
                    </div>

                    {/* Tenders Stats */}
                    <div className="flex items-center justify-between bg-emerald-50/50 p-2 rounded-lg">
                      <div className="flex items-center gap-1.5 text-emerald-700">
                        <TrendingUp className="w-3.5 h-3.5" />
                        <span className="text-[10px] uppercase font-bold tracking-wider">Tenders</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <div className="text-gray-600"><span className="font-bold text-gray-900">{tenderTotal}</span> Total</div>
                        <div className="text-gray-600"><span className="font-bold text-gray-900">{tenderAwarded}</span> Awarded</div>
                        <div className={`font-bold ${tenderRate >= 20 ? 'text-green-600' : tenderRate > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {tenderRate.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {sources.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          {t('noSourcesYet')}
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {sourceToDelete && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSourceToDelete(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 animate-scale-in" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Source</h3>
            <p className="text-gray-500 text-sm mb-6">
              Are you sure you want to delete <span className="font-semibold text-gray-800">&quot;{sourceToDelete.name}&quot;</span>? This action cannot be undone and may affect prequalifications associated with this source.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setSourceToDelete(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await onDelete(sourceToDelete.id);
                  setSourceToDelete(null);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
